import { Component, lazy, Suspense, useEffect, useState, type ErrorInfo, type ReactNode, type ComponentType } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import { CookieConsentProvider, useCookieConsent } from "@/contexts/CookieConsentContext";
import { ORGANIZATION_JSON_LD } from "@/components/landing/SEO";
import { MARKETING_PAGES } from "@/components/landing/flags";
import landingI18n, { isLandingLanguageLoaded, loadLandingLanguage, loadLegalTexts, type LandingLang } from "@/lib/i18n-landing";
import CookieBanner from "@/components/landing/CookieBanner";
import RouteFallback from "@/components/RouteFallback";
import { isAppPath } from "@/lib/app-paths";
import { captureException, whenSentryReady } from "@/lib/sentry-loader";
// Landing y utilidades ligeras: eager (camino crítico de la home + fallback).
import Landing from "./pages/Landing";
import NotFound from "./pages/NotFound";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import ClarityLoader from "@/lib/clarity-loader";
import MetaPixelLoader from "@/lib/meta-pixel-loader";
import { revokeMetaPixel } from "@/lib/meta-pixel";

// LDT-718 · Los avisos (sonner) se cargan tras montar: ver ToasterTrasMontar.
const Sonner = lazy(() => import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })));

// LDT-718 · La aplicación (panel, app de familias, calendario, recuperación…)
// vive en src/Plataforma.tsx con sus providers y sus rutas, y solo se descarga
// cuando la URL es suya (isAppPath). La landing no la arrastra.
const Plataforma = lazy(() => import("./Plataforma"));

// Spokes del sitio público: lazy, cada una en su trozo.
// LDT-717 las había hecho eager porque createRoot tiraba el HTML prerenderizado
// y, con lazy(), la página se quedaba en blanco hasta bajar su trozo (CLS 0,88
// en /features). LDT-718 hidrata (main.tsx): React conserva el HTML del
// prerender mientras llega el trozo de la página, así que vuelven a lazy y la
// portada no descarga el código de precios, contacto o legales.
const PricingPage = lazy(() => import("./pages/Pricing"));
const ContactPage = lazy(() => import("./pages/Contact"));
const AboutPage = lazy(() => import("./pages/About"));
const FeaturesPage = lazy(() => import("./pages/Features"));
// LDT-741 · Cada página legal espera también sus textos en castellano, que ya
// no viajan en el trozo de entrada (i18n-landing.ts, loadLegalTexts): así
// están cargados antes de pintar, al hidratar y en el prerender.
function legalLazy<T extends { default: ComponentType }>(load: () => Promise<T>) {
  return lazy(() => Promise.all([load(), loadLegalTexts()]).then(([m]) => m));
}
const PrivacyPage = legalLazy(() => import("./pages/legal/Privacy"));
const TermsPage = legalLazy(() => import("./pages/legal/Terms"));
const CookiesPage = legalLazy(() => import("./pages/legal/Cookies"));
const LegalNoticePage = legalLazy(() => import("./pages/legal/LegalNotice"));
const RefundsPage = legalLazy(() => import("./pages/legal/Refunds"));
const DataDeletionPage = legalLazy(() => import("./pages/legal/DataDeletion"));
// LDT-700 · Portada /legal: prerenderizada como las demás legales.
const LegalIndexPage = legalLazy(() => import("./pages/legal/LegalIndex"));
// LDT-701 · Contrato de encargo del tratamiento, prerenderizado como las demás.
const DpaPage = legalLazy(() => import("./pages/legal/Dpa"));
// LDT-702 · Subencargados y seguridad, spokes hermanas del DPA.
const SubprocessorsPage = legalLazy(() => import("./pages/legal/Subprocessors"));
const SecurityPage = legalLazy(() => import("./pages/legal/Security"));
// LDT-711 · El texto del consentimiento para el dato de salud (familias).
const HealthConsentPage = legalLazy(() => import("./pages/legal/HealthConsent"));
// …pero SU CSS no: si viajara con el trozo lazy, el HTML prerenderizado se
// pintaría sin estilos hasta que llegara (CLS 1,8 en /features, medido en el
// preview de LDT-718). Importado aquí, va en la hoja principal que enlaza el
// <head> de cada página, como cuando las spokes eran eager.
import "@/components/landing/about-page.css";
import "@/components/landing/contact-page.css";
import "./pages/features-page.css";
// Sin prerender (tras su flag): lazy, en su chunk.
const ForChainsPage = lazy(() => import("./pages/ForChains"));




// Set de rutas públicas (paths RELATIVOS), reutilizado tal cual para es (raíz,
// sin prefijo), /ca y /en bajo <LangLayout>. `k` evita colisiones de key entre
// los tres montajes. Mantener sincronizado con la lista del sitemap.
function publicRoutes(k: string) {
  return [
    <Route key={`${k}-home`} index element={<Landing />} />,
    <Route key={`${k}-contact`} path="contact" element={<Suspense fallback={null}><ContactPage /></Suspense>} />,
    <Route key={`${k}-about`} path="about-us" element={<Suspense fallback={null}><AboutPage /></Suspense>} />,
    <Route key={`${k}-privacy`} path="privacy" element={<Suspense fallback={null}><PrivacyPage /></Suspense>} />,
    <Route key={`${k}-terms`} path="terms" element={<Suspense fallback={null}><TermsPage /></Suspense>} />,
    <Route key={`${k}-cookies`} path="cookies" element={<Suspense fallback={null}><CookiesPage /></Suspense>} />,
    <Route key={`${k}-legal-notice`} path="legal-notice" element={<Suspense fallback={null}><LegalNoticePage /></Suspense>} />,
    <Route key={`${k}-refunds`} path="refunds" element={<Suspense fallback={null}><RefundsPage /></Suspense>} />,
    <Route key={`${k}-data-deletion`} path="data-deletion" element={<Suspense fallback={null}><DataDeletionPage /></Suspense>} />,
    <Route key={`${k}-legal`} path="legal" element={<Suspense fallback={null}><LegalIndexPage /></Suspense>} />,
    <Route key={`${k}-dpa`} path="dpa" element={<Suspense fallback={null}><DpaPage /></Suspense>} />,
    <Route key={`${k}-subprocessors`} path="subprocessors" element={<Suspense fallback={null}><SubprocessorsPage /></Suspense>} />,
    <Route key={`${k}-security`} path="security" element={<Suspense fallback={null}><SecurityPage /></Suspense>} />,
    <Route key={`${k}-health-consent`} path="health-consent" element={<Suspense fallback={null}><HealthConsentPage /></Suspense>} />,
    <Route key={`${k}-pricing`} path="pricing" element={MARKETING_PAGES.pricing ? <Suspense fallback={null}><PricingPage /></Suspense> : <Navigate to="/" replace />} />,
    <Route key={`${k}-chain`} path="chain" element={MARKETING_PAGES.chain ? <Suspense fallback={null}><ForChainsPage /></Suspense> : <Navigate to="/" replace />} />,
    <Route key={`${k}-features`} path="features" element={MARKETING_PAGES.features ? <Suspense fallback={null}><FeaturesPage /></Suspense> : <Navigate to="/" replace />} />,
  ];
}

// Sincroniza el idioma de i18n con el de la URL (fuente de verdad). Se hace de
// forma síncrona en el render —idempotente y guardado por el `if`— para que el
// primer render (y el HTML del futuro prerender) ya salga en el idioma correcto.
// Los recursos de los 3 idiomas están precargados, así que changeLanguage es
// efectivamente síncrono. El selector de idioma ya cambia el idioma antes de
// navegar, por lo que aquí suele ser un no-op.
function LangLayout({ lang }: { lang: LandingLang }) {
  // LDT-718 · Catalán e inglés llegan en su propio trozo. Al entrar por URL ya
  // vienen cargados (main.tsx y el prerender los cargan antes de pintar); al
  // navegar a otro idioma, se suspende hasta tenerlos, en vez de pintar las
  // claves o el castellano de respaldo.
  if (!isLandingLanguageLoaded(lang)) throw loadLandingLanguage(lang);
  if (landingI18n.language !== lang) {
    void landingI18n.changeLanguage(lang);
  }
  return <Outlet />;
}

// Rutas de la plataforma de admin que se movieron bajo /platform/<pagina>.
// Las rutas viejas (raíz) se mantienen como redirect de retrocompatibilidad
// para PWAs instaladas (start_url antiguo /login), marcadores y deep links.
// reset-password NO entra aquí: se queda en la raíz (destino del email de
// recuperación + allowlist de Supabase Auth). "login" sí: /login redirige a
// /platform/login preservando hash/query para PWAs instaladas y magic links.
const LEGACY_ADMIN_PATHS = [
  "login", "home", "dashboard", "sessions", "products",
  "families", "bonos", "employees", "settings", "event-types", "events",
  "memberships", "manual", "super",
];

// Redirect que PRESERVA el hash y la query. Clave para los flujos de auth que
// llegan a /login o /reset-password con el token en el fragmento
// (#access_token=…): un <Navigate to="/platform/…"> normal lo perdería.
function LegacyRedirect({ to }: { to: string }) {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
}

// Vercel Analytics + SpeedInsights + Microsoft Clarity se montan SÓLO si
// el usuario consintió la categoría "analytics". Sin consentimiento
// (estado inicial o "rechazar todo") ninguno de los scripts se carga,
// cumpliendo RGPD/ePrivacy. ClarityLoader es un no-op si la env var
// VITE_CLARITY_PROJECT_ID no está definida.
// LDT-105 · PROB-316: el replay de Sentry sigue el mismo gating que el resto
// de analíticas — no arranca hasta que haya consentimiento.
// LDT-718 · Sentry se carga aparte (src/lib/sentry-loader.ts): se espera a que
// esté listo, y el módulo del replay también llega con import(). En la landing
// Sentry solo se carga con un error, así que ahí no hay replay.
function ConsentedAnalytics() {
  const { choice } = useCookieConsent();
  useEffect(() => {
    if (!choice?.analytics) return;
    void whenSentryReady()
      .then((Sentry) => Sentry && import("@/lib/sentry-replay"))
      .then((m) => m && m.enableSentryReplayIfConsented(choice?.analytics));
  }, [choice?.analytics]);

  if (!choice?.analytics) return null;
  return (
    <>
      <Analytics />
      <SpeedInsights />
      <ClarityLoader />
    </>
  );
}

// LDT-692 (LDT-474 · PROB-793): el Píxel de Meta es publicidad, así que se
// monta SÓLO con consentimiento de MARKETING, independiente del de analítica.
// Cualquier script de marketing futuro va aquí dentro, nunca al lado.
// Sin marketing (sin decidir, rechazado o revocado ahora mismo) se llama a
// revokeMetaPixel(): corta el envío si el Píxel ya estaba cargado y borra
// _fbp/_fbc — lo que Clarity no hace al revocar (LDT-460).
// Exportado para testearlo sin montar la app entera (src/test/).
export function ConsentedMarketing() {
  const { choice } = useCookieConsent();
  const marketing = !!choice?.marketing;
  useEffect(() => {
    if (!marketing) revokeMetaPixel();
  }, [marketing]);

  if (!marketing) return null;
  return <MetaPixelLoader />;
}

// Exportado (además de usado abajo) para poder testear el patrón de forma
// aislada sin montar todo el árbol de la app — ver src/test/.
//
// El patrón `_result.default` cubre un TypeError de React.lazy que NO llega
// como ChunkLoadError: "undefined is not an object (evaluating
// 'e._result.default')" (Sentry PLAYIUM-WEB-22/23/25/28).
//
// Aquí decía que la causa era "la build de prerender". Era falso, y conviene
// dejarlo escrito para que no se vuelva a diagnosticar mal: el nombre
// entry-prerender-*.js que aparece en los stacks es solo cómo bautiza Rollup
// el chunk común (el segundo entry declarado), y ese fichero ES el bundle
// principal de la app. La causa real era el `event.preventDefault()` del
// listener de vite:preloadError en main.tsx, que hacía resolver la promesa del
// import() con undefined en lugar de rechazar. Ver el comentario de ahí.
//
// Corregido eso, esta cláusula queda muerta. Se deja como cinturón y tirantes
// por si a alguien le tarda en llegar la build nueva.
// `is not a valid ... MIME type` cubre la redacción de Safari/WebKit cuando
// el catch-all de vercel.json devuelve `index.html` (Content-Type text/html)
// para un chunk con hash caducado en vez de un 404 real (Sentry
// PLAYIUM-WEB-34, segunda ocurrencia del hueco que ya predijo PROB-777).
export const CHUNK_ERROR_PATTERN =
  /import(ing)? a module script failed|dynamically imported module|ChunkLoadError|Loading chunk|module script failed|Unable to preload|_result\.default|is not a valid .*mime type/i;

// Fallback raíz. Si el error es de carga de un chunk (build caducada tras un
// deploy), recargamos automáticamente para coger la versión nueva; si no, se
// muestra el mensaje genérico con un botón de recargar.
function AppErrorFallback({ error }: { error: unknown }) {
  const msg = String((error as { message?: string } | null)?.message ?? error ?? "");
  const isChunkError = CHUNK_ERROR_PATTERN.test(msg);
  useEffect(() => {
    if (!isChunkError) return;
    const KEY = "__chunk_reload_ts";
    const last = Number(sessionStorage.getItem(KEY) || 0);
    if (Date.now() - last < 20000) return; // evita bucle si el chunk no carga de verdad
    sessionStorage.setItem(KEY, String(Date.now()));
    window.location.reload();
  }, [isChunkError]);
  return (
    <div style={{ padding: "2rem", textAlign: "center", fontFamily: "system-ui, sans-serif" }}>
      <p>{isChunkError ? "Actualizando la aplicación…" : "Ha ocurrido un error inesperado."}</p>
      <button onClick={() => window.location.reload()} style={{ marginTop: "1rem", padding: "0.5rem 1rem", borderRadius: 8, border: "1px solid #ccc", cursor: "pointer" }}>
        Recargar
      </button>
    </div>
  );
}

// LDT-718 · El suelo de todo, sin el SDK de Sentry en el trozo de la landing:
// hace lo que hacía `Sentry.ErrorBoundary` (reportar y enseñar
// AppErrorFallback), con el Sentry que se carga aparte (src/lib/sentry-loader).
class AppErrorBoundary extends Component<{ children: ReactNode }, { error: unknown }> {
  state = { error: null as unknown };

  static getDerivedStateFromError(error: unknown) {
    return { error };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    captureException(error, { contexts: { react: { componentStack: info.componentStack } } });
  }

  render() {
    if (this.state.error !== null) return <AppErrorFallback error={this.state.error} />;
    return this.props.children;
  }
}

// LDT-718 · Los avisos (sonner) no están en el HTML prerenderizado: montarlos
// en el primer render descuadraría la hidratación. Entran justo después, y su
// código llega en su propio trozo.
function ToasterTrasMontar() {
  const [montado, setMontado] = useState(false);
  useEffect(() => {
    // En la app, al momento: sus pantallas avisan nada más cargar. En la
    // landing solo avisa el formulario de la portada, tras enviarlo: basta con
    // tenerlo a la primera interacción, y así una visita que solo lee no
    // descarga sonner (~10 KB).
    if (isAppPath(window.location.pathname)) {
      setMontado(true);
      return;
    }
    const montar = () => setMontado(true);
    const eventos = ["pointerdown", "keydown", "focusin"] as const;
    for (const e of eventos) window.addEventListener(e, montar, { once: true, passive: true });
    return () => {
      for (const e of eventos) window.removeEventListener(e, montar);
    };
  }, []);
  return montado ? (
    <Suspense fallback={null}>
      <Sonner />
    </Suspense>
  ) : null;
}

const App = () => (
  <AppErrorBoundary>
  <HelmetProvider>
      {/* Sitewide Schema.org Organization. Cada spoke añade luego su
          propio bloque (Product, ItemList, BreadcrumbList, …) vía el
          componente <SEO/>. */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(ORGANIZATION_JSON_LD)}
        </script>
      </Helmet>
      {/* Future flags activadas (opt-in v7). Silencia los dos warnings que
          aparecían en la consola de dev:
           - v7_startTransition: envuelve actualizaciones de estado del router
             en `React.startTransition` (mejor concurrencia con Suspense).
           - v7_relativeSplatPath: resolución de rutas relativas dentro de
             routes con splat (`*`) que cambia en v7. Activarlo ahora evita
             sorpresas cuando llegue v7 y elimina el warning a la vez. */}
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AppInner />
      </BrowserRouter>
      <ToasterTrasMontar />
  </HelmetProvider>
  </AppErrorBoundary>
);

// Árbol interno: todo lo que va DENTRO del router. Se reutiliza tal cual en el
// cliente (bajo <BrowserRouter> aquí) y en el prerender (bajo <StaticRouter> en
// src/entry-prerender.tsx), manteniendo contextos y rutas en un único sitio.
// Al cambiar de página, arriba del todo.
//
// El navegador solo resetea el scroll cuando carga un documento nuevo, y en
// una SPA la navegación no lo es: React Router cambia el árbol pero deja la
// ventana donde estaba. Efecto: si ibas por la mitad de la home y pulsabas
// "Precios", /pricing se abría por la mitad — a veces en una sección
// cualquiera, a veces en el pie si la página destino era más corta.
//
// Dos excepciones deliberadas:
//   · Navegación POP (atrás/adelante del navegador). Ahí el visitante espera
//     volver justo donde estaba, y el propio navegador restaura la posición.
//   · URLs con hash (/features#qr desde el desplegable del header). El
//     destino es el ancla, no el principio: se busca el elemento tras el
//     render y se salta a él DE GOLPE. Nada de `smooth` aquí — quien pulsa
//     "Pagos online" en el desplegable pide el módulo 9, y animarlo sería
//     hacerle ver desfilar los ocho anteriores. El scroll suave es para los
//     saltos dentro de una misma página, que además ya resuelve el
//     navegador con los <a href="#…"> del propio índice.
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    if (navigationType === "POP") return;

    if (hash) {
      // El elemento aún no existe en el momento del efecto si la página
      // entra por Suspense; un frame de margen basta y evita meter un
      // MutationObserver por un caso tan acotado.
      const id = hash.slice(1);
      requestAnimationFrame(() => {
        document.getElementById(id)?.scrollIntoView({ block: "start" });
      });
      return;
    }

    window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
  }, [pathname, hash, navigationType]);

  return null;
}

export function AppInner() {
  const { pathname } = useLocation();
  return (
        <CookieConsentProvider>
          <ScrollToTop />
          <ConsentedAnalytics />
          <ConsentedMarketing />
          <CookieBanner />
          {/* LDT-718 · Las rutas de la app (src/lib/app-paths.ts) las pinta
              Plataforma, con sus providers (sesión, Dexie, branding, React
              Query). Aquí solo queda el sitio público. */}
          {isAppPath(pathname) ? (
            <Suspense fallback={<RouteFallback />}>
              <Plataforma />
            </Suspense>
          ) : (
            <Suspense fallback={<RouteFallback />}>
            <Routes>
              {/* ── Sitio público multi-idioma ────────────────────────────────
                  ES (default) sin prefijo; CA e EN bajo /ca y /en. <LangLayout>
                  sincroniza el idioma de i18n desde la URL (fuente de verdad) y
                  publicRoutes() define el set de páginas una sola vez. La home
                  pública es el index de cada layout (/, /ca, /en). */}
              <Route element={<LangLayout lang="es" />}>{publicRoutes("es")}</Route>
              <Route path="ca" element={<LangLayout lang="ca" />}>{publicRoutes("ca")}</Route>
              <Route path="en" element={<LangLayout lang="en" />}>{publicRoutes("en")}</Route>

              <Route path="/landing-preview" element={<Navigate to="/" replace />} />
              {/* Redirects desde slugs ES (Stream C v1) hacia los slugs EN
                  definitivos (founder-decisions §7.2). React Router no emite
                  HTTP 301 (es client-side); para SEO real, replicar este
                  mapping a nivel de hosting (vercel.json `redirects`). El
                  `replace` evita dejar la URL vieja en el history. */}
              <Route path="/precios" element={<Navigate to="/pricing" replace />} />
              <Route path="/solicitar-demo" element={<Navigate to="/contact" replace />} />
              {/* /book-demo se retiró con el pivot self-serve: la reunión previa
                  ya no existe. Se conserva el redirect porque la URL estuvo
                  indexada y enlazada desde emails. */}
              <Route path="/book-demo" element={<Navigate to="/pricing" replace />} />
              <Route path="/para-cadenas" element={<Navigate to="/chain" replace />} />
              <Route path="/sobre-nosotros" element={<Navigate to="/about-us" replace />} />
              <Route path="/privacidad" element={<Navigate to="/privacy" replace />} />
              <Route path="/terminos" element={<Navigate to="/terms" replace />} />

              {/* Retrocompatibilidad: rutas viejas de admin → /platform/* (preserva
                  hash/query para los flujos de auth). Cubre PWAs instaladas con
                  start_url antiguo, marcadores y deep links. */}
              {LEGACY_ADMIN_PATHS.map((p) => (
                <Route key={p} path={`/${p}`} element={<LegacyRedirect to={`/platform/${p}`} />} />
              ))}
              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
          )}
        </CookieConsentProvider>
  );
}

export default App;
